// Validation utilities for List Parser frontend

export interface ValidationError {
  rowIndex: number;
  column: string;
  columnIndex: number;
  errorType: ValidationErrorType;
  message: string;
}

export enum ValidationErrorType {
  MISSING_DATA = 'MISSING_DATA',
  INVALID_QUANTITY = 'INVALID_QUANTITY',
  EMPTY_ROW = 'EMPTY_ROW',
  INVALID_FORMAT = 'INVALID_FORMAT',
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warningCount: number;
  errorCount: number;
  summary: ValidationSummary;
}

export interface ValidationSummary {
  totalRows: number;
  validRows: number;
  rowsWithMissingData: number;
  emptyRows: number;
  rowsWithInvalidQuantity: number;
  rowsWithInvalidFormat: number;
}

export interface ColumnMappings {
  partNumber: number;
  description: number;
  quantity: number;
  machine?: number;
}

type CellValue = string | number | boolean | Date | null;
type RowData = CellValue[];
type TableData = RowData[];

export const validateTableData = (
  data: TableData,
  columnMappings: ColumnMappings,
  hasHeaderRow: boolean = true
): ValidationResult => {
  const errors: ValidationError[] = [];
  const summary: ValidationSummary = {
    totalRows: 0,
    validRows: 0,
    rowsWithMissingData: 0,
    emptyRows: 0,
    rowsWithInvalidQuantity: 0,
    rowsWithInvalidFormat: 0,
  };

  // Skip header row if present
  const startRow = hasHeaderRow ? 1 : 0;
  const dataRows = data.slice(startRow);
  summary.totalRows = dataRows.length;

  dataRows.forEach((row, index) => {
    const actualRowIndex = index + startRow;
    const rowErrors: ValidationError[] = [];

    // Check for completely empty rows
    const isEmpty = row.every(
      (cell) =>
        cell === null ||
        cell === undefined ||
        (typeof cell === 'string' && cell.trim() === '')
    );

    if (isEmpty) {
      summary.emptyRows++;
      rowErrors.push({
        rowIndex: actualRowIndex,
        column: 'all',
        columnIndex: -1,
        errorType: ValidationErrorType.EMPTY_ROW,
        message: 'Row is completely empty',
      });
    } else {
      // Validate required columns
      const partNumber = getCellValue(row, columnMappings.partNumber - 1);
      const description = getCellValue(row, columnMappings.description - 1);
      const quantity = getCellValue(row, columnMappings.quantity - 1);

      // Check for missing part number
      if (isEmptyValue(partNumber)) {
        summary.rowsWithMissingData++;
        rowErrors.push({
          rowIndex: actualRowIndex,
          column: 'partNumber',
          columnIndex: columnMappings.partNumber - 1,
          errorType: ValidationErrorType.MISSING_DATA,
          message: 'Part number is missing',
        });
      } else if (!isValidPartNumber(partNumber)) {
        summary.rowsWithInvalidFormat++;
        rowErrors.push({
          rowIndex: actualRowIndex,
          column: 'partNumber',
          columnIndex: columnMappings.partNumber - 1,
          errorType: ValidationErrorType.INVALID_FORMAT,
          message: 'Part number format is invalid',
        });
      }

      // Check for missing description
      if (isEmptyValue(description)) {
        summary.rowsWithMissingData++;
        rowErrors.push({
          rowIndex: actualRowIndex,
          column: 'description',
          columnIndex: columnMappings.description - 1,
          errorType: ValidationErrorType.MISSING_DATA,
          message: 'Description is missing',
        });
      }

      // Check for missing or invalid quantity
      if (isEmptyValue(quantity)) {
        summary.rowsWithMissingData++;
        rowErrors.push({
          rowIndex: actualRowIndex,
          column: 'quantity',
          columnIndex: columnMappings.quantity - 1,
          errorType: ValidationErrorType.MISSING_DATA,
          message: 'Quantity is missing',
        });
      } else if (!isValidQuantity(quantity)) {
        summary.rowsWithInvalidQuantity++;
        rowErrors.push({
          rowIndex: actualRowIndex,
          column: 'quantity',
          columnIndex: columnMappings.quantity - 1,
          errorType: ValidationErrorType.INVALID_QUANTITY,
          message: 'Quantity must be a positive number',
        });
      }
    }

    if (rowErrors.length === 0) {
      summary.validRows++;
    }

    errors.push(...rowErrors);
  });

  const isValid = errors.length === 0;
  const warningCount = errors.filter(
    (e) =>
      e.errorType === ValidationErrorType.MISSING_DATA ||
      e.errorType === ValidationErrorType.EMPTY_ROW
  ).length;
  const errorCount = errors.filter(
    (e) =>
      e.errorType === ValidationErrorType.INVALID_QUANTITY ||
      e.errorType === ValidationErrorType.INVALID_FORMAT
  ).length;

  return {
    isValid,
    errors,
    warningCount,
    errorCount,
    summary,
  };
};

const getCellValue = (row: RowData, columnIndex: number): CellValue => {
  if (columnIndex < 0 || columnIndex >= row.length) {
    return null;
  }
  return row[columnIndex];
};

const isEmptyValue = (value: CellValue): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const isValidPartNumber = (value: CellValue): boolean => {
  if (isEmptyValue(value)) return false;
  const str = String(value).trim();
  // Part number should have some meaningful content (not just whitespace or special chars)
  return str.length >= 1 && /[a-zA-Z0-9]/.test(str);
};

const isValidQuantity = (value: CellValue): boolean => {
  if (isEmptyValue(value)) return false;

  const num = typeof value === 'number' ? value : Number(String(value).trim());
  return !isNaN(num) && num > 0 && isFinite(num);
};

export const getValidationErrorsByRow = (
  errors: ValidationError[]
): Map<number, ValidationError[]> => {
  const errorsByRow = new Map<number, ValidationError[]>();

  errors.forEach((error) => {
    const existing = errorsByRow.get(error.rowIndex) || [];
    existing.push(error);
    errorsByRow.set(error.rowIndex, existing);
  });

  return errorsByRow;
};

export const getValidationErrorsByCell = (
  errors: ValidationError[]
): Map<string, ValidationError> => {
  const errorsByCell = new Map<string, ValidationError>();

  errors.forEach((error) => {
    const key = `${error.rowIndex}-${error.columnIndex}`;
    errorsByCell.set(key, error);
  });

  return errorsByCell;
};
